import { useState, useEffect } from 'react'
import i18n from '../../../i18n'
import { localizeApiDetail } from '../../../i18n/apiError'
import { bruktbilfinansieringAPI } from '../../../services/api'
import type {
  HendelseDealerRef,
  HendelserResponse,
  HendelseSource,
  HendelseType,
} from '../../../types/bruktbilfinansiering'

export interface HendelserQuery {
  startDate: string
  endDate: string
  type: HendelseType | ''
  source: HendelseSource | ''
  dealerOrgNr: string
  page: number
  pageSize: number
}

function message(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
  if (typeof detail === 'string') return localizeApiDetail(detail) || i18n.t('errors.unknown', { ns: 'bbf' })
  return e instanceof Error ? e.message : i18n.t('errors.unknown', { ns: 'bbf' })
}

/** Dealer events for the bank's BankDealerAgent network. */
export function useHendelser(bankOrgNr: string, q: HendelserQuery) {
  const [data, setData] = useState<HendelserResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    bruktbilfinansieringAPI.getHendelser(bankOrgNr, {
      start_date: q.startDate,
      end_date: q.endDate,
      type: q.type || undefined,
      source: q.source || undefined,
      dealer_org_nr: q.dealerOrgNr || undefined,
      page: q.page,
      page_size: q.pageSize,
    })
      .then(res => { if (!cancelled) setData(res.data) })
      .catch((e: unknown) => { if (!cancelled) setError(message(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [bankOrgNr, q.startDate, q.endDate, q.type, q.source, q.dealerOrgNr, q.page, q.pageSize])

  return { data, loading, error }
}

/**
 * The dealerships the bank subscribes to, for the Forhandler filter.
 *
 * Deliberately independent of the selected period: this reads BankDealerAgent,
 * not the event tables, so a dealer with no events in the window is still
 * offered. A filter that hides the option you are looking for is worse than one
 * that returns nothing.
 */
export function useHendelserForhandlere(bankOrgNr: string) {
  const [dealers, setDealers] = useState<HendelseDealerRef[]>([])

  useEffect(() => {
    let cancelled = false
    bruktbilfinansieringAPI.getHendelserForhandlere(bankOrgNr)
      .then(res => { if (!cancelled) setDealers(res.data.dealers) })
      .catch(() => { if (!cancelled) setDealers([]) })
    return () => { cancelled = true }
  }, [bankOrgNr])

  return dealers
}
